/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { dataLevelType } from '../../registry/constants';
import { defaultValue } from './ActionSetData';

const ActionSetDataManager: ActionManagerFn<'ActionSetData'> = (
  platform: Platform,
): ActionManager<'ActionSetData'> => {
  return {
    ...baseActionManager(platform, 'ActionSetData'),
    defaultValue,
    props: {
      ...baseActionProps,
      dataLevel: {
        label: 'Level',
        type: 'select',
        options: dataLevelType,
        required: true,
      },
      key: {
        label: 'Key',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        helpText: (item: RequiredItem<'ActionSetData'>) =>
          `Once set, you can later access this value using the expression $.$${
            item.props.dataLevel
          }Data.${
            (typeof item.props.key === 'string'
              ? item.props.key
              : item.props.key.value) || '<key>'
          }`,
      },
      value: {
        label: 'Value',
        type: 'expression',
        returnType: async () => 'any',
        required: true,
      },
    },
    name: 'Set Data',
    getDisplayText: (item) =>
      typeof item.props.key === 'string'
        ? item.props.key
        : item.props.key.value,
    description: `**Overview**<br/>
The Set Current Record action is a state management component designed to define the "active" context within a Data Store. In the platform's data binding architecture, a Data Store often holds many records, but expressions like #{EmployeeDS.Name} only refer to one specific record at a time: the "Current Record." This action programmatically updates that pointer.<br/><br/>
By executing this action, developers instruct the Data Store to focus on a specific record identified by its ID. Consequently, any UI components (like Form fields, Detail cards, or Labels) that are bound to that store's attributes will immediately update to display the data from this newly designated record.

**Key Features**<br/>
* **Context Switching:** Dynamically changes the data source context for the entire page or section without requiring a new database query.
* **Binding Synchronization:** Instantly refreshes all single-record UI components bound to the target Alias, ensuring they display the details of the selected item.
* **Dynamic Resolution:** Uses a recordIdExpression to identify the target, allowing the application to set the context based on variables, URL parameters, or logic (e.g., "Set current to the first record").
* **Broad Compatibility:** Works across all major store types (CloudDataStore, DataStore, LocalDataStore, ReadOnlyDataStore, TransientDataStore), making it the standard way to handle Master-Detail navigation.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the Data Store whose active record pointer will be updated.
* **Record ID Expression (Required):** An expression property returning a string.
  * *Function:* The unique identifier (Row ID) of the record to become the "Current" record.

**Use Cases**<br/>
* **Master-Detail Navigation:** When a user clicks a row in an "Employee List" grid, this action sets that employee as the "Current Record" in the EmployeeDS, causing the "Employee Details" form next to the grid to populate with their information.
* **Deep Linking:** On page load, reading an ID from the URL query string (param.id) and using this action to set the relevant record as active, ensuring the user sees the specific item they shared.
* **Default Selection:** Automatically setting the first record in a list as "current" immediately after the data loads (TriggerAfterLoad).

Data set at Page level can only be accessed within the same page using the following expression
\`\`\`javascript
$.$pageData.<key>
\`\`\`

Data set at Global level can be accessed accross pages using the following expression
\`\`\`javascript
$.$globalData.<key>
\`\`\`

\`\`\`alert
Note: Replace <key> with the value specified for the Key property
\`\`\`

`,
  };
};

export default ActionSetDataManager;
